
const libPictProvider = require('pict-provider');
const libConfigSync = require('./ConfigSync.js');

// An Observation Type is a relational graph: one ObservationManifest (Name / DisplayName / Category /
// IDCustomer) plus an ordered set of ObservationRequirementSpecification rows, each pointing at a
// shared ArtifactType. Journal vs Spreadsheet is NOT stored here — it is chosen per project when the
// type is associated (a later phase), and that association references the manifest BY NAME. So Name
// is load-bearing: renaming a type orphans its project associations, and cloning to a new Name is the
// safe way to make a variant. These are plain Meadow entity records, tenant-scoped by IDCustomer
// (0 = platform/default; a customer id = that customer's bespoke types).

// The persisted columns we round-trip on the manifest (audit + GUID are server-owned).
const MANIFEST_FIELDS = [ 'IDObservationManifest', 'Name', 'DisplayName', 'Category', 'IDCustomer', 'SettingsJSON' ];
// The persisted columns we round-trip on a requirement (GUID + audit are server-owned).
const REQUIREMENT_FIELDS = [ 'IDObservationRequirementSpecification', 'IDObservationManifest', 'IDArtifactType', 'Name', 'CountMinimum', 'CountMaximum', 'Required', 'DefaultJSON' ];

const DEFAULT_COUNT_MAXIMUM = 100;

/**
 * ObservationTypeEditor — the data layer behind the bespoke Observation Type builder.
 *
 * Loads a manifest and its requirement rows, exposes a working model the view edits, and persists
 * create / edit / clone back through the Meadow REST API. The pict-section-recordset list stays the
 * browse surface; this provider drives the bespoke record editor. State lives in
 * pict.AppData.ObservationTypeEditor; the view derives + repaints declaratively.
 */
class ObservationTypeEditorProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The shared ArtifactType library (cached): the array + lookup maps by id and by name.
		this.artifactTypes = [];
		this.artifactByID = {};
		this.artifactByName = {};

		// Every manifest (cached) for category suggestions + Name-uniqueness checks.
		this._allManifests = null;

		// The working editor model (null until a load / new / clone). Held outside AppData so the
		// view derives a clean render-state from it each paint.
		this._editor = null;

		// Monotonic key for working requirement rows (stable handler references across re-renders).
		this._rowKeySeed = 1;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	// --- Session context + write-permission (mirrors the master-list tool / the server's gate) ---

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get idUser()
	{
		return this.currentUser.UserID;
	}

	/** A customer-1 / role-5 super-user manages platform (IDCustomer 0) types and anything. */
	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	get hasCustomerAdmin()
	{
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin'));
	}

	/**
	 * Can the current user edit a type owned by this customer? A platform (IDCustomer 0) type is
	 * editable only by a super-user; a customer's own types by that customer; super-users edit all.
	 * @param {Number} pIDCustomer
	 * @return {Boolean}
	 */
	canEditCustomer(pIDCustomer)
	{
		if (this.isSuperUser) { return true; }
		return Number(pIDCustomer) === Number(this.idCustomer);
	}

	/**
	 * May the current user manage observation types AT ALL? A super-user always can (and edits the platform
	 * IDCustomer-0 defaults). A customer admin granted the "Manage observation types" entitlement (the
	 * ObservationManifest-Create capability) can manage their OWN customer's types — the per-type
	 * canEditCustomer() gate in deriveRenderState still keeps the shared platform defaults (IDCustomer 0) and
	 * any other customer's types read-only for them (they Clone to customize). The server enforces the same
	 * boundary (HeadlightController.checkAndInferRecordIDCustomer + ObservationManifestBundle
	 * .enforceRequirementSpecCustomerScope), so this is UI parity, not the only gate. Fail-CLOSED until the
	 * capability map has loaded — a write surface must never flash enabled before we can confirm the grant.
	 * @return {Boolean}
	 */
	get isAdmin()
	{
		if (this.isSuperUser) { return true; }
		const tmpService = this.pict.fable && this.pict.fable.EffectivePermissionsService;
		if (!tmpService || (typeof tmpService.canHash !== 'function') || (typeof tmpService.currentUserReady !== 'function')) { return false; }
		if (!tmpService.currentUserReady()) { return false; }
		return !!tmpService.canHash('ObservationManifest-Create');
	}

	// --- REST helpers (promise wrappers over the Headlight rest client) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	// Observation-type WRITES go through a credentialed fetch carrying `x-application-name: HeadLight`. That header
	// opts the request into the API's capability-based permission check (ObservationManifest-Create / Observation
	// RequirementSpecification-Create|Update|Delete, granted via the "Manage observation types" entitlement) plus
	// the server's per-customer IDCustomer stamping. WITHOUT it the config app is seen by the API as "Fieldbook"
	// (not a permission-enforcing app) and these writes fall through to the legacy schema authorizer, where
	// ObservationManifest.Create = Deny → 403 {Error:'UNAUTHORIZED ACCESS IS NOT ALLOWED', ErrorCode:405}. This is
	// SCOPED to this editor on purpose — a stopgap until the config app is made fully permission-enforcing for all
	// of its writes; every other config-app request keeps its current (rest-client) behavior. Reads are unaffected
	// (their schema level is Read = Allow), so only writes carry the header. The session cookie authenticates the
	// fetch, exactly as _deleteJSON already relied on. See HeadlightController.allowPermissionServiceCheck.
	_appHeaders() { return { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-application-name': 'HeadLight' }; }

	_apiPrefix() { return (this.restClient && this.restClient.options && this.restClient.options.ServerURL) || '/1.0/'; }

	async _writeJSON(pMethod, pURL, pPayload)
	{
		const tmpResponse = await fetch(`${this._apiPrefix()}${pURL}`, { method: pMethod, credentials: 'include', headers: this._appHeaders(), body: JSON.stringify(pPayload) });
		let tmpBody = null; try { tmpBody = await tmpResponse.json(); } catch (pError) { /* a non-JSON/empty body is acceptable for some writes */ }
		if (!tmpResponse.ok || (tmpBody && tmpBody.Error)) { throw new Error((tmpBody && tmpBody.Error) || `${pMethod} ${pURL} failed (${tmpResponse.status})`); }
		return tmpBody;
	}

	_postJSON(pURL, pPayload) { return this._writeJSON('POST', pURL, pPayload); }

	_putJSON(pURL, pPayload) { return this._writeJSON('PUT', pURL, pPayload); }

	/**
	 * Delete a record (the Headlight rest client has no delete verb). Carries the same app-name header as the
	 * other writes so the API uses the capability check, not the legacy schema authorizer.
	 * @param {String} pURL - entity path under /1.0/ (e.g. 'ObservationRequirementSpecification/42').
	 * @return {Promise<void>}
	 */
	_deleteJSON(pURL)
	{
		return fetch(`${this._apiPrefix()}${pURL}`, { method: 'DELETE', credentials: 'include', headers: this._appHeaders() })
			.then((pResponse) =>
			{
				if (!pResponse.ok) { throw new Error(`DELETE ${pURL} failed (${pResponse.status})`); }
			});
	}

	// --- ArtifactType library ---

	/**
	 * Load + cache the ArtifactType library (the data-shapes a requirement can collect).
	 * @return {Promise<Array<Object>>}
	 */
	async loadArtifactTypesAsync()
	{
		if (this.artifactTypes.length > 0)
		{
			return this.artifactTypes;
		}
		const tmpRows = await this._getJSON('ArtifactTypes');
		this.artifactTypes = (Array.isArray(tmpRows) ? tmpRows : []).filter((pRow) => pRow && (pRow.Deleted !== 1));
		this.artifactByID = {};
		this.artifactByName = {};
		this.artifactTypes.forEach((pArtifact) =>
		{
			this.artifactByID[pArtifact.IDArtifactType] = pArtifact;
			this.artifactByName[pArtifact.Name] = pArtifact;
		});
		return this.artifactTypes;
	}

	/**
	 * Parse an ArtifactType's JSON-Schema `Schemata` into an ordered column list.
	 * @param {Object} pArtifact
	 * @return {Array<{ Key: String, DisplayName: String, Type: String }>}
	 */
	_artifactColumns(pArtifact)
	{
		if (!pArtifact || !pArtifact.Schemata) { return []; }
		let tmpSchema = pArtifact.Schemata;
		if (typeof tmpSchema === 'string')
		{
			try { tmpSchema = JSON.parse(tmpSchema); }
			catch (pError) { return []; }
		}
		const tmpProperties = (tmpSchema && tmpSchema.properties) || {};
		return Object.keys(tmpProperties)
			.map((pKey) => ({ Key: pKey, DisplayName: tmpProperties[pKey].displayName || pKey, Type: tmpProperties[pKey].type || 'string', Ordinal: tmpProperties[pKey].ordinal }))
			.sort((pA, pB) => ((pA.Ordinal == null ? 999 : pA.Ordinal) - (pB.Ordinal == null ? 999 : pB.Ordinal)));
	}

	/**
	 * A short human summary of an artifact's shape for the requirement row.
	 * @param {Number} pIDArtifactType
	 * @return {String}
	 */
	_artifactSummary(pIDArtifactType)
	{
		const tmpArtifact = this.artifactByID[pIDArtifactType];
		if (!tmpArtifact) { return ''; }
		const tmpColumns = this._artifactColumns(tmpArtifact);
		if (tmpColumns.length === 0) { return tmpArtifact.Name; }
		const tmpNames = tmpColumns.map((pColumn) => pColumn.Key);
		const tmpShown = tmpNames.slice(0, 5).join(', ') + ((tmpNames.length > 5) ? `, +${tmpNames.length - 5} more` : '');
		return `${tmpColumns.length} field${tmpColumns.length === 1 ? '' : 's'}: ${tmpShown}`;
	}

	// --- Preview (render a type's requirements as a Journal form or a Spreadsheet grid) ---

	/**
	 * The input kind a property previews as: select / checkbox / number / text.
	 * @param {Object} pProperty
	 * @return {String}
	 */
	_inputKindFor(pProperty)
	{
		if (Array.isArray(pProperty.enumOptions) && (pProperty.enumOptions.length > 0)) { return 'select'; }
		const tmpType = (`${pProperty.type || 'string'}`).toLowerCase();
		if (tmpType === 'boolean') { return 'checkbox'; }
		if ((tmpType === 'number') || (tmpType === 'integer') || (tmpType === 'double')) { return 'number'; }
		return 'text';
	}

	/**
	 * The visible, ordered fields of an artifact type, shaped for the preview (label + input kind).
	 * @param {Number} pIDArtifactType
	 * @return {Array<Object>}
	 */
	_artifactFields(pIDArtifactType)
	{
		const tmpArtifact = this.artifactByID[pIDArtifactType];
		if (!tmpArtifact) { return []; }
		let tmpSchema = tmpArtifact.Schemata;
		if (typeof tmpSchema === 'string') { try { tmpSchema = JSON.parse(tmpSchema); } catch (pError) { return []; } }
		const tmpProperties = (tmpSchema && tmpSchema.properties) || {};
		return Object.keys(tmpProperties)
			.map((pKey) => Object.assign({ Key: pKey }, tmpProperties[pKey]))
			.filter((pProperty) => (pProperty.visible !== false) && (pProperty.Hidden !== true))
			.sort((pA, pB) => ((pA.ordinal == null ? 999 : pA.ordinal) - (pB.ordinal == null ? 999 : pB.ordinal)))
			.map((pProperty) =>
			{
				const tmpKind = this._inputKindFor(pProperty);
				return (
					{
						Key: pProperty.Key,
						Label: pProperty.displayName || pProperty.Key,
						Kind: tmpKind,
						MultiField: false,
						IsSelect: (tmpKind === 'select'),
						IsCheckbox: (tmpKind === 'checkbox'),
						IsText: (tmpKind === 'text') || (tmpKind === 'number'),
						InputType: (tmpKind === 'number') ? 'number' : 'text',
						Options: (Array.isArray(pProperty.enumOptions) ? pProperty.enumOptions : []).map((pOption) => ({ Value: pOption }))
					});
			});
	}

	setPreviewMode(pMode)
	{
		this.editorState.PreviewMode = (pMode === 'spreadsheet') ? 'spreadsheet' : 'journal';
		this._render();
	}

	// --- Manifest catalog (categories + name uniqueness) ---

	async _ensureAllManifestsAsync()
	{
		if (this._allManifests) { return this._allManifests; }
		let tmpRows = [];
		try { tmpRows = await this._getJSON('ObservationManifests'); }
		catch (pError) { this.log.warn(`ObservationTypeEditor: could not load manifest catalog: ${pError.message || pError}`); }
		this._allManifests = Array.isArray(tmpRows) ? tmpRows : [];
		return this._allManifests;
	}

	_categories()
	{
		const tmpSet = {};
		(this._allManifests || []).forEach((pManifest) =>
		{
			if (pManifest && pManifest.Category) { tmpSet[pManifest.Category] = true; }
		});
		return Object.keys(tmpSet).sort();
	}

	/**
	 * Is this Name already used by a different manifest the current user can see? (Associations key
	 * on Name, so it must be unique to be safe.)
	 * @param {String} pName
	 * @param {Number} [pExcludeID]
	 * @return {Boolean}
	 */
	_nameInUse(pName, pExcludeID)
	{
		const tmpName = (pName || '').trim().toLowerCase();
		return (this._allManifests || []).some((pManifest) =>
			pManifest && (pManifest.Deleted !== 1) && ((pManifest.Name || '').trim().toLowerCase() === tmpName)
			&& (Number(pManifest.IDObservationManifest) !== Number(pExcludeID)));
	}

	// --- Working-model construction ---

	_blankRequirement(pSeed)
	{
		const tmpSeed = pSeed || {};
		return (
			{
				_key: this._rowKeySeed++,
				IDObservationRequirementSpecification: tmpSeed.IDObservationRequirementSpecification || null,
				IDArtifactType: (tmpSeed.IDArtifactType != null) ? tmpSeed.IDArtifactType : null,
				Name: tmpSeed.Name || '',
				CountMinimum: (tmpSeed.CountMinimum != null) ? tmpSeed.CountMinimum : 0,
				CountMaximum: (tmpSeed.CountMaximum != null) ? tmpSeed.CountMaximum : DEFAULT_COUNT_MAXIMUM,
				Required: !!(tmpSeed.Required === 1 || tmpSeed.Required === true),
				DefaultJSON: (tmpSeed.DefaultJSON != null) ? tmpSeed.DefaultJSON : ''
			});
	}

	/**
	 * Load an existing type into the editor (by ID — robust against the duplicate GUIDs in legacy data).
	 * @param {Number} pIDManifest
	 * @return {Promise<void>}
	 */
	async loadAsync(pIDManifest)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();

		try
		{
			await this.loadArtifactTypesAsync();
			await this._ensureAllManifestsAsync();

			const tmpManifest = await this._getJSON(`ObservationManifest/${pIDManifest}`);
			if (!tmpManifest || !tmpManifest.IDObservationManifest)
			{
				throw new Error(`Observation type ${pIDManifest} not found.`);
			}
			const tmpRequirements = await this._getJSON(`ObservationRequirementSpecifications/FilteredTo/FBV~IDObservationManifest~EQ~${tmpManifest.IDObservationManifest}`);

			this._editor =
			{
				mode: this.isAdmin ? 'edit' : 'view',
				manifest: tmpManifest,
				originalName: tmpManifest.Name,
				requirements: (Array.isArray(tmpRequirements) ? tmpRequirements : []).map((pRow) => this._blankRequirement(pRow)),
				removedRequirementIDs: []
			};
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the observation type: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	/**
	 * Start a brand-new type in the editor.
	 * @param {Number} [pIDCustomer] - tenant scope (defaults to the user's customer).
	 * @return {Promise<void>}
	 */
	async newType(pIDCustomer)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		this._render();

		await this.loadArtifactTypesAsync();
		await this._ensureAllManifestsAsync();

		this._editor =
		{
			mode: 'new',
			manifest: { Name: '', DisplayName: '', Category: '', IDCustomer: (pIDCustomer != null) ? pIDCustomer : this.idCustomer, SettingsJSON: '{}' },
			originalName: null,
			requirements: [],
			removedRequirementIDs: []
		};
		tmpState.Loading = false;
		this._render();
	}

	/**
	 * Stage a clone of an existing type as a new (unsaved) type — copy its content, mint a unique
	 * Name, and land in the editor so the user can review before saving. Saving creates the manifest
	 * and its requirement rows fresh (new ids / GUIDs, repointed to the new manifest).
	 * @param {Number} pIDSource
	 * @param {Number} [pIDCustomer] - tenant scope for the clone (defaults to the user's customer).
	 * @return {Promise<void>}
	 */
	async cloneFrom(pIDSource, pIDCustomer)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();

		try
		{
			await this.loadArtifactTypesAsync();
			await this._ensureAllManifestsAsync();

			const tmpSource = await this._getJSON(`ObservationManifest/${pIDSource}`);
			if (!tmpSource || !tmpSource.IDObservationManifest)
			{
				throw new Error(`Source observation type ${pIDSource} not found.`);
			}
			const tmpRequirements = await this._getJSON(`ObservationRequirementSpecifications/FilteredTo/FBV~IDObservationManifest~EQ~${tmpSource.IDObservationManifest}`);

			this._editor =
			{
				mode: 'new',
				manifest:
				{
					Name: this._uniqueName(`${tmpSource.Name} (copy)`),
					DisplayName: tmpSource.DisplayName || '',
					Category: tmpSource.Category || '',
					IDCustomer: (pIDCustomer != null) ? pIDCustomer : this.idCustomer,
					SettingsJSON: tmpSource.SettingsJSON || '{}'
				},
				originalName: null,
				// Copy requirement content WITHOUT ids so save() creates them fresh, repointed to the new manifest.
				requirements: (Array.isArray(tmpRequirements) ? tmpRequirements : []).map((pRow) =>
					this._blankRequirement({ IDArtifactType: pRow.IDArtifactType, Name: pRow.Name, CountMinimum: pRow.CountMinimum, CountMaximum: pRow.CountMaximum, Required: pRow.Required, DefaultJSON: pRow.DefaultJSON })),
				removedRequirementIDs: [],
				clonedFrom: tmpSource.Name
			};
			tmpState.Loading = false;
			tmpState.Message = `Cloned from “${tmpSource.Name}”. Review and Save to create it.`;
			tmpState.MessageClass = 'is-info';
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not clone: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	/**
	 * A Name not currently used by any visible manifest (appends " 2", " 3", … if needed).
	 * @param {String} pBase
	 * @return {String}
	 */
	_uniqueName(pBase)
	{
		let tmpName = (pBase || 'Untitled').slice(0, 30);
		let tmpCounter = 2;
		while (this._nameInUse(tmpName))
		{
			const tmpSuffix = ` ${tmpCounter}`;
			tmpName = `${(pBase || 'Untitled').slice(0, 30 - tmpSuffix.length)}${tmpSuffix}`;
			tmpCounter++;
		}
		return tmpName;
	}

	// --- Persistence ---

	/**
	 * Persist the working editor: create-or-update the manifest, then create / update / delete its
	 * requirement rows, then reload. Guards Name (non-empty, unique, rename warning).
	 * @return {Promise<void>}
	 */
	async saveAsync()
	{
		if (!this._editor) { return; }
		const tmpState = this.editorState;
		const tmpManifest = this._editor.manifest;

		const tmpName = (tmpManifest.Name || '').trim();
		if (!tmpName)
		{
			tmpState.Message = 'A Name is required.';
			tmpState.MessageClass = 'is-warning';
			this._render();
			return;
		}
		if (this._nameInUse(tmpName, tmpManifest.IDObservationManifest))
		{
			tmpState.Message = `The name “${tmpName}” is already used by another observation type. Names must be unique (project associations reference types by name).`;
			tmpState.MessageClass = 'is-warning';
			this._render();
			return;
		}

		// Rename guard: an existing type's Name is referenced by project associations BY NAME.
		if ((this._editor.mode === 'edit') && this._editor.originalName && (tmpName !== this._editor.originalName))
		{
			const tmpModal = this.pict.views['Pict-Section-Modal'];
			const tmpConfirmed = (tmpModal && typeof tmpModal.confirm === 'function')
				? await tmpModal.confirm(`Renaming “${this._editor.originalName}” to “${tmpName}” will break any project associations that reference it by name. Continue?`,
					{ title: 'Rename observation type?', confirmLabel: 'Rename', cancelLabel: 'Cancel', dangerous: true })
				: true;
			if (!tmpConfirmed) { return; }
		}

		tmpState.Saving = true;
		tmpState.Message = '';
		this._render();

		try
		{
			// 1. Manifest.
			let tmpSavedManifest;
			if (tmpManifest.IDObservationManifest)
			{
				tmpSavedManifest = await this._putJSON('ObservationManifest', this._pick(tmpManifest, MANIFEST_FIELDS));
			}
			else
			{
				const tmpCreate = this._pick(tmpManifest, MANIFEST_FIELDS);
				delete tmpCreate.IDObservationManifest;
				tmpSavedManifest = await this._postJSON('ObservationManifest', tmpCreate);
			}
			const tmpIDManifest = (tmpSavedManifest && tmpSavedManifest.IDObservationManifest) || tmpManifest.IDObservationManifest;

			// 2. Requirements: delete removed, then upsert the working rows.
			for (const tmpRemovedID of this._editor.removedRequirementIDs)
			{
				await this._deleteJSON(`ObservationRequirementSpecification/${tmpRemovedID}`);
			}
			for (const tmpRow of this._editor.requirements)
			{
				const tmpRecord = this._requirementRecord(tmpRow, tmpIDManifest);
				if (tmpRow.IDObservationRequirementSpecification)
				{
					await this._putJSON('ObservationRequirementSpecification', tmpRecord);
				}
				else
				{
					delete tmpRecord.IDObservationRequirementSpecification;
					await this._postJSON('ObservationRequirementSpecification', tmpRecord);
				}
			}

			// 3. Refresh the catalog + reload the (now-persisted) type.
			this._allManifests = null;
			tmpState.Saving = false;
			await this.loadAsync(tmpIDManifest);
			this.editorState.Message = `Saved “${tmpName}”.`;
			this.editorState.MessageClass = 'is-success';
			this._render();

			// Creating/changing an Observation Type makes the published observation-manifest config stale until a
			// sync runs. Fire a SCOPED sync for just this type: Customer scope (which also refreshes the customer's
			// projects) for a tenant type, or the super-user-only Global scope for a platform default (IDCustomer 0).
			const tmpSyncCustomerID = Number((tmpSavedManifest && tmpSavedManifest.IDCustomer) != null ? tmpSavedManifest.IDCustomer : tmpManifest.IDCustomer);
			if (tmpSyncCustomerID > 0)
			{
				libConfigSync.requestScopedSync(this.pict, 'Customer', tmpSyncCustomerID, { manifest: tmpName });
			}
			else
			{
				libConfigSync.requestScopedSync(this.pict, 'Global', 0, { manifest: tmpName });
			}

			if (typeof window !== 'undefined' && window.location)
			{
				window.location.hash = `#/ObservationType/${tmpIDManifest}`;
			}
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Save failed: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	_pick(pObject, pFields)
	{
		const tmpResult = {};
		pFields.forEach((pField) => { if (pObject[pField] !== undefined) { tmpResult[pField] = pObject[pField]; } });
		return tmpResult;
	}

	_requirementRecord(pRow, pIDManifest)
	{
		return (
			{
				IDObservationRequirementSpecification: pRow.IDObservationRequirementSpecification || undefined,
				IDObservationManifest: pIDManifest,
				IDArtifactType: pRow.IDArtifactType,
				Name: (pRow.Name || '').trim(),
				CountMinimum: Number(pRow.CountMinimum) || 0,
				CountMaximum: Number(pRow.CountMaximum) || 0,
				Required: pRow.Required ? 1 : 0,
				DefaultJSON: (pRow.DefaultJSON && (`${pRow.DefaultJSON}`).trim().length > 0) ? pRow.DefaultJSON : null
			});
	}

	// --- Working-model mutations (called from the view) ---

	_row(pKey)
	{
		return this._editor ? this._editor.requirements.find((pRow) => pRow._key === Number(pKey)) : null;
	}

	setManifestField(pField, pValue)
	{
		if (this._editor) { this._editor.manifest[pField] = pValue; }
	}

	addRequirement()
	{
		if (!this._editor) { return; }
		this._editor.requirements.push(this._blankRequirement());
		this._render();
	}

	removeRequirement(pKey)
	{
		if (!this._editor) { return; }
		const tmpRow = this._row(pKey);
		if (!tmpRow) { return; }
		const tmpApply = () =>
		{
			if (tmpRow.IDObservationRequirementSpecification)
			{
				this._editor.removedRequirementIDs.push(tmpRow.IDObservationRequirementSpecification);
			}
			this._editor.requirements = this._editor.requirements.filter((pRow) => pRow._key !== tmpRow._key);
			this._render();
		};
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && (typeof tmpModal.confirm === 'function'))
		{
			tmpModal.confirm(`Remove the “${tmpRow.Name || 'unnamed'}” requirement?`,
				{ title: 'Remove requirement', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { tmpApply(); } });
		}
		else { tmpApply(); }
	}

	setRequirementArtifact(pKey, pArtifactName)
	{
		const tmpRow = this._row(pKey);
		if (!tmpRow) { return; }
		const tmpArtifact = this.artifactByName[pArtifactName];
		tmpRow.IDArtifactType = tmpArtifact ? tmpArtifact.IDArtifactType : null;
		// First time naming a requirement, default its Name to the artifact's name for convenience.
		if (tmpArtifact && !tmpRow.Name) { tmpRow.Name = tmpArtifact.Name; }
		this._render();
	}

	setRequirementField(pKey, pField, pValue)
	{
		const tmpRow = this._row(pKey);
		if (!tmpRow) { return; }
		if (pField === 'Required') { tmpRow.Required = !!pValue; }
		else if ((pField === 'CountMinimum') || (pField === 'CountMaximum')) { tmpRow[pField] = Number(pValue) || 0; }
		else { tmpRow[pField] = pValue; }
	}

	// --- Render-state (drives the ObservationTypeEditor view via AppData) ---

	get editorState()
	{
		if (!this.pict.AppData.ObservationTypeEditor)
		{
			this.pict.AppData.ObservationTypeEditor =
			{
				Loading: false, Saving: false, Message: '', MessageClass: '',
				LoadingSlot: [], EditorSlot: [], EmptySlot: [ {} ], MessageSlot: [],
				Mode: '', CanEdit: false, ScopeLabel: '', ReadOnlyNoticeSlot: [], ClonedNoticeSlot: [],
				Name: '', DisplayName: '', Category: '', RequirementCount: 0,
				Requirements: [], Categories: [], ArtifactOptions: [],
				SaveDisabledAttr: '', TitleText: 'Observation Type',
				PreviewMode: 'journal', PreviewJournal: [], PreviewColumns: [], PreviewRows: [],
				PreviewJournalSlot: [], PreviewSpreadsheetSlot: [], PreviewEmptySlot: [ {} ],
				JournalBtnClass: 'is-link is-selected', SpreadsheetBtnClass: '',
				ProjectsSlot: [], ProjectsHintSlot: []
			};
		}
		return this.pict.AppData.ObservationTypeEditor;
	}

	/**
	 * The anchor for the embedded Projects association editor: a SAVED manifest's Name (associations
	 * key on the manifest by name), or null for a new / clone-in-progress type (nothing to anchor on
	 * yet — the view shows a "save first" hint instead).
	 * @return {String|null}
	 */
	get projectsAnchorName()
	{
		if (!this._editor || (this._editor.mode === 'new')) { return null; }
		const tmpManifest = this._editor.manifest;
		return (tmpManifest && tmpManifest.IDObservationManifest && tmpManifest.Name) ? tmpManifest.Name : null;
	}

	deriveRenderState()
	{
		const tmpState = this.editorState;

		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];
		tmpState.ArtifactOptions = this.artifactTypes
			.slice()
			.sort((pA, pB) => (pA.Name || '').localeCompare(pB.Name || ''))
			.map((pArtifact) => ({ Name: pArtifact.Name }));
		tmpState.Categories = this._categories().map((pCategory) => ({ Name: pCategory }));

		if (tmpState.Loading)
		{
			tmpState.LoadingSlot = [ {} ];
			tmpState.EditorSlot = [];
			tmpState.EmptySlot = [];
			return;
		}
		if (!this._editor)
		{
			tmpState.LoadingSlot = [];
			tmpState.EditorSlot = [];
			tmpState.EmptySlot = [ {} ];
			return;
		}

		const tmpManifest = this._editor.manifest;
		// Can the user edit THIS type? They must be able to manage types at all (isAdmin) AND own it: a NEW type
		// is created for the user's own customer, but an EXISTING one is editable only by its owning customer
		// (canEditCustomer) — so the shared platform (IDCustomer 0) defaults and any other customer's types stay
		// read-only for a customer admin, who Clones them to customize. Super-users pass canEditCustomer for all.
		const tmpCanEdit = this.isAdmin
			&& ((this._editor.mode === 'new')
				|| ((this._editor.mode === 'edit') && this.canEditCustomer(tmpManifest.IDCustomer)));

		tmpState.Mode = this._editor.mode;
		tmpState.CanEdit = tmpCanEdit;
		tmpState.ReadOnlyAttr = tmpCanEdit ? '' : 'disabled';
		tmpState.Name = tmpManifest.Name || '';
		tmpState.DisplayName = tmpManifest.DisplayName || '';
		tmpState.Category = tmpManifest.Category || '';
		tmpState.TitleText = (this._editor.mode === 'new')
			? 'New Observation Type'
			: `${tmpManifest.DisplayName || tmpManifest.Name || 'Observation Type'}`;

		const tmpScope = (Number(tmpManifest.IDCustomer) === 0) ? 'Platform default' : `Customer ${tmpManifest.IDCustomer}`;
		tmpState.ScopeLabel = tmpScope;
		// The read-only notice shows whenever the current type can't be edited — a user without the capability,
		// OR a customer admin viewing a shared/global or another customer's type (who can still Clone it below).
		tmpState.ReadOnlyNoticeSlot = (!tmpCanEdit) ? [ { ScopeLabel: tmpScope } ] : [];
		tmpState.ClonedNoticeSlot = (this._editor.clonedFrom) ? [ { ClonedFrom: this._editor.clonedFrom } ] : [];
		tmpState.CanClone = this.isAdmin && (this._editor.mode === 'edit');

		tmpState.Requirements = this._editor.requirements.map((pRow) =>
			(
				{
					Key: pRow._key,
					Name: pRow.Name || '',
					ArtifactName: (this.artifactByID[pRow.IDArtifactType] || {}).Name || '',
					ArtifactSummary: this._artifactSummary(pRow.IDArtifactType),
					ArtifactMissing: (pRow.IDArtifactType == null),
					RequiredAttr: pRow.Required ? 'checked' : '',
					CountMinimum: pRow.CountMinimum,
					CountMaximum: pRow.CountMaximum,
					DefaultJSON: pRow.DefaultJSON || '',
					ReadOnlyAttr: tmpCanEdit ? '' : 'disabled'
				}));
		tmpState.RequirementCount = this._editor.requirements.length;
		tmpState.HasRequirements = (this._editor.requirements.length > 0);
		tmpState.EmptyRequirementsSlot = (this._editor.requirements.length > 0) ? [] : [ {} ];

		// Live preview — the same requirements rendered as a Journal form or a Spreadsheet grid.
		if (!tmpState.PreviewMode) { tmpState.PreviewMode = 'journal'; }
		const tmpPreviewReqs = this._editor.requirements.filter((pRow) => (pRow.IDArtifactType != null));
		tmpState.PreviewJournal = tmpPreviewReqs.map((pRow) =>
		{
			const tmpFields = this._artifactFields(pRow.IDArtifactType);
			const tmpMulti = (tmpFields.length > 1);
			tmpFields.forEach((pField) => { pField.MultiField = tmpMulti; });
			const tmpArtifact = this.artifactByID[pRow.IDArtifactType] || {};
			return (
				{
					Label: pRow.Name || '(unnamed)',
					RequiredFlag: !!pRow.Required,
					RepeatSlot: (Number(pRow.CountMaximum) > 1) ? [ { Text: `repeats up to ${pRow.CountMaximum}` } ] : [],
					Fields: tmpFields,
					CaptureSlot: (tmpFields.length === 0) ? [ { ArtifactName: tmpArtifact.Name || 'data' } ] : []
				});
		});
		const tmpColumns = [];
		tmpPreviewReqs.forEach((pRow) =>
		{
			const tmpFields = this._artifactFields(pRow.IDArtifactType);
			if (tmpFields.length <= 1)
			{
				const tmpField = tmpFields[0] || { Kind: 'text', IsText: true, IsSelect: false, IsCheckbox: false, InputType: 'text', Options: [] };
				tmpColumns.push(Object.assign({ Header: pRow.Name || '(unnamed)' }, tmpField));
			}
			else
			{
				tmpFields.forEach((pField) => { tmpColumns.push(Object.assign({}, pField, { Header: `${pRow.Name ? pRow.Name + ' · ' : ''}${pField.Label}` })); });
			}
		});
		tmpState.PreviewColumns = tmpColumns;
		tmpState.PreviewRows = [ { Cells: tmpColumns.slice() }, { Cells: tmpColumns.slice() } ];
		const tmpPreviewEmpty = (tmpPreviewReqs.length === 0);
		tmpState.PreviewEmptySlot = tmpPreviewEmpty ? [ {} ] : [];
		tmpState.PreviewJournalSlot = (!tmpPreviewEmpty && (tmpState.PreviewMode === 'journal')) ? [ {} ] : [];
		tmpState.PreviewSpreadsheetSlot = (!tmpPreviewEmpty && (tmpState.PreviewMode === 'spreadsheet')) ? [ {} ] : [];
		tmpState.JournalBtnClass = (tmpState.PreviewMode === 'journal') ? 'is-link is-selected' : '';
		tmpState.SpreadsheetBtnClass = (tmpState.PreviewMode === 'spreadsheet') ? 'is-link is-selected' : '';

		// Projects-using-this-type: the OTHER side of the ProjectObservationManifest association,
		// anchored on this manifest's Name. Only for a saved type (associations key on the name); a
		// new / clone-in-progress type shows a "save first" hint instead. The view paints the embedded
		// association editor into the container after render.
		const tmpAnchor = this.projectsAnchorName;
		tmpState.ProjectsSlot = tmpAnchor ? [ {} ] : [];
		tmpState.ProjectsHintSlot = tmpAnchor ? [] : [ {} ];

		tmpState.SaveDisabledAttr = (tmpState.Saving || !tmpCanEdit) ? 'disabled' : '';
		tmpState.LoadingSlot = [];
		tmpState.EmptySlot = [];
		tmpState.EditorSlot = [ tmpState ];
	}

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.ObservationTypeEditor)
		{
			this.pict.views.ObservationTypeEditor.render();
		}
	}

	/**
	 * Open a type by its GUID, resolving to the row's real ID first (robust against the duplicate
	 * GUIDs in legacy data). Bridges the pict-section-recordset list (which links by GUID) to the
	 * editor's ID-based load.
	 * @param {String} pGUID
	 * @return {Promise<void>}
	 */
	async openByGUIDAsync(pGUID)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpRows = await this._getJSON(`ObservationManifests/FilteredTo/FBV~GUIDObservationManifest~EQ~${pGUID}`);
			const tmpRecord = (Array.isArray(tmpRows) && tmpRows.length > 0) ? tmpRows[0] : null;
			if (!tmpRecord) { throw new Error(`No observation type found for GUID ${pGUID}.`); }
			await this.loadAsync(tmpRecord.IDObservationManifest);
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not open the observation type: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	/** Navigate to a record's edit route (used by the "Clone to my customer" / back actions). */
	openType(pIDManifest)
	{
		if (typeof window !== 'undefined' && window.location)
		{
			window.location.hash = `#/ObservationType/${pIDManifest}`;
		}
	}

	cloneCurrentToMyCustomer()
	{
		if (this._editor && this._editor.manifest && this._editor.manifest.IDObservationManifest)
		{
			this.cloneFrom(this._editor.manifest.IDObservationManifest, this.idCustomer);
		}
	}

	backToList()
	{
		if (typeof window !== 'undefined' && window.location)
		{
			window.location.hash = '#/PSRS/ObservationManifest/List';
		}
	}
}

module.exports = ObservationTypeEditorProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ObservationTypeEditor',
	AutoInitialize: true,
	AutoInitializeOrdinal: 210
};
